import { forwardRef } from 'react'
import { EcommerceBannerPlacement, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { cleanBrandTitle } from '../../utils/cleanBrandTitle'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import { localizeCurrency } from '../functions'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import style from './Card.module.scss'
import commonStyle from '../Chewy.module.scss'
import Rating from './Rating'
import AddToCartButton from './AddToCartButton'
import Carousel from '../../components/Carousel'
import { useBannerImpression } from '../../hooks/useBannerImpression'
import newCustomersTagIcon from '../assets/icons/new-customers-tag.svg'
import byChewyLogo from '../assets/icons/by-chewy-logo.svg'
import { hiddenClass } from '@aytm/app/swidget/base/questions/Ecommerce/utils/hiddenClass.ts'

interface ICardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  findability: boolean
  amount?: number
  disabled?: boolean
  showAddToCart?: boolean
  bordered?: boolean
  compact?: boolean
  enableImageGallery?: boolean
  'data-testid'?: string
  bannerPlacement?: EcommerceBannerPlacement
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt?: number) => void
  onSetQuantity: (index: number, qnt: number) => void
}

const Card = forwardRef<HTMLDivElement, ICardProps>(
  (
    {
      schema,
      answer,
      findability,
      amount = 0,
      disabled,
      showAddToCart = true,
      bordered = false,
      compact = false,
      enableImageGallery = true,
      'data-testid': dataTestId,
      bannerPlacement,
      onItemSelect,
      onAddToCart,
      onSetQuantity
    },
    ref
  ) => {
    const { t, languageTag } = useSurveyLocale()
    const viewport = useViewport()
    // Grid cards switch to a 40/60 image-beside-content row on mobile; bordered cards
    // (sponsored carousel) keep the vertical tile layout.
    const mobileRow = viewport.mobile && !bordered
    // Compact mobile tiles (sponsored carousel) — smaller type/padding, keep the column layout.
    const mobileCompact = viewport.mobile && compact
    // Called unconditionally per the Rules of Hooks; a null placement short-circuits inside it. The
    // two refs never meet: only the grid registers a card, and the grid is not a banner placement.
    const bannerRef = useBannerImpression(answer.id, bannerPlacement ?? null)
    const selectProduct = () =>
      bannerPlacement ? onItemSelect(answer.index, bannerPlacement) : onItemSelect(answer.index)
    const combinedRef = bannerPlacement ? bannerRef : ref
    const brand = answer.sub_title ? cleanBrandTitle(answer.sub_title) : ''
    const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
    const discountPercent = hasDiscount
      ? Math.round(((answer.price - answer.discounted_price) / answer.price) * 100)
      : 0

    return (
      <div
        ref={combinedRef}
        data-testid={dataTestId}
        aria-disabled={disabled || false}
        className={`${style.card} ${hiddenClass(answer)} ${bordered ? style.cardBordered : ''} ${disabled ? style.cardDisabled : ''} ${mobileRow ? style.mobile : ''} ${mobileCompact ? style.mobileCompact : ''}`}
      >
        <div className={style.imageWrapper} onClick={selectProduct}>
          {/* Grid card shows a single badge: red_label takes precedence, overal_pick ("New") is the fallback. */}
          {(answer.red_label || answer.overal_pick) && (
            <div className={commonStyle.badgeRow}>
              {/* HTML by design (author markup / search highlight); XSS-safe — sanitized server-side on ingress: author via SurveySchemaTexts.safe_string?, import via Trajectdata::DataSanitizer. */}
              {answer.red_label ? (
                <div className={commonStyle.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
              ) : (
                <div className={commonStyle.newBadge} />
              )}
            </div>
          )}

          <EmptyLink color="#1C49C4">
            <button
              type="button"
              className={style.heart}
              onClick={e => e.stopPropagation()}
              aria-label={t('questions', 'ec', 'chewy', 'addToWishlist')}
            >
              <i className="pi pi-heart" />
            </button>
          </EmptyLink>

          {enableImageGallery ? (
            <Carousel schema={answer} store="chewy" pagination="none" controls={!viewport.mobile} logEvents={false} />
          ) : answer.image ? (
            <div className={style.image} style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }} />
          ) : (
            <div className={style.noImage} />
          )}

          {answer.sponsored && (
            <div className={commonStyle.sponsored}>{t('questions', 'ec', 'chewy', 'sponsored')}</div>
          )}
        </div>

        <div className={style.info}>
          <div className={style.body} onClick={selectProduct}>
            <div
              className={style.title}
              dangerouslySetInnerHTML={{
                __html: `${brand ? `<b>${brand}</b> ` : ''}${answer.text}`
              }}
            />

            {answer.amazon_choice && (
              <div className={commonStyle.byChewy}>
                <img src={byChewyLogo} alt="Chewy" className={commonStyle.byChewyLogo} />
              </div>
            )}

            <div className={style.ratingRow}>
              {viewport.mobile && <span className={style.ratingValue}>{(answer.rating ?? 0).toFixed(1)}</span>}
              <Rating rating={answer.rating} />
              {answer.reviews ? <span className={style.reviewsCount}>({answer.reviews})</span> : null}
            </div>

            <div className={style.priceRow}>
              {hasDiscount ? (
                <>
                  <span
                    className={`${style.priceCurrent} ${style.priceCurrentDiscounted}`}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(
                        answer.discounted_price,
                        schema.price_mask,
                        languageTag,
                        true,
                        false
                      )
                    }}
                  />
                  <span
                    className={style.priceOld}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                    }}
                  />
                  <span className={style.discountText}>
                    {t<(percent: number) => string>('questions', 'ec', 'chewy', 'discountOff')(discountPercent)}
                  </span>
                </>
              ) : (
                <span
                  className={style.priceCurrent}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                  }}
                />
              )}
            </div>

            <div className={style.promoLine}>
              <img src={newCustomersTagIcon} alt="" role="presentation" className={style.promoIcon} />
              <span>{localizeCurrency(t('questions', 'ec', 'chewy', 'newCustomersOffer'), schema.price_mask)}</span>
            </div>

            <div className={style.returns}>{t('questions', 'ec', 'chewy', 'freeReturns365')}</div>
          </div>

          {showAddToCart && (
            <div className={style.cta} onClick={e => e.stopPropagation()}>
              <AddToCartButton
                index={answer.index}
                findability={findability}
                amount={amount}
                limit={schema.max_qty_per_product}
                onAddToCart={onAddToCart}
                onSetQuantity={onSetQuantity}
              />
            </div>
          )}
        </div>
      </div>
    )
  }
)

Card.displayName = 'ChewyCard'
export default Card
